import { defineStore } from 'pinia'
import { ref, computed, reactive, watch } from 'vue'
import { useNotification } from '@/composables/useNotification'
import { getWorkingLanguageId } from '@/utils/handy'
import { ET, useEditorControl } from '@/composables/useEditorControl'

// type
import {
  ACTIVATE_REGISTRY_TRANSLATION,
  GET_CATEGORIES,
  GET_CATEGORY_ITEMS,
  MARK_REGISTRY_TRANSLATIONS,
  DELETE_REGISTRY,
  DELETE_REGISTRY_TRANSLATION,
  UPDATE_REGISTRY,
  UPDATE_REGISTRY_ITEM_STATUS,
  DELETE_CATEGORY
} from '@/graphql/registryQueries'
import { InfoCell, SelectOptionType } from '@/types/viewFieldTypes'
import {
  Category,
  CodeType,
  Registry,
  RegistryTranslation,
  UpdateRegistryInputType,
  RegistryEditBuffer
} from '@/types/registryTypes'

// methods
import { executeGraphQL } from '@/utils/graphqlUtils'
import { localeEntry } from '@/utils/handy'

//
export const useRegistryStore = defineStore('registry', () => {
  const { addNotification, setLoading } = useNotification()

  // variables
  const categories = ref<Category[]>([])
  const selectedCategory = ref<Category>() // reference to the target category
  const selectedRegistry = ref<Registry>() // reference to the target registry
  const selectedTranslation = ref<RegistryTranslation>() // reference to the target translation

  const ac = useEditorControl({})

  // watch(
  //   () => categories.value,
  //   (newCategories) => {
  //     console.log('Categories updated:', JSON.stringify(newCategories))
  //   },
  //   { deep: true }
  // )
  // Database API -------------------------------------------------------------
  // download only category headers
  const queryCategories = async () => {
    // if it is already downloaded, don't do it again
    // console.log(
    //   'registryStore: queryCategories: categories.value.length: ',
    //   categories.value.length
    // )
    // if (categories.value.length > 0) {
    if (categories.value.length > 0) {
      //console.log('categories is already downloaded, using it')
      return
    }

    setLoading(true)
    try {
      const data = await executeGraphQL(GET_CATEGORIES)

      if (data && data.getRegistryCategories) {
        // const registries: Registry[] = data.getRegistryCategories;
        // processing concept is
        // using a Map object to manipulate data in Array, then convert into an object.
        const categoriesMap = new Map<number, Category>()

        data.getRegistryCategories.forEach((registry: Registry) => {
          if (!categoriesMap.has(registry.category)) {
            categoriesMap.set(registry.category, {
              header: { ...registry },
              // items will be filled later upon retrieving items
              items: []
            })
          }

          // if the category is already in the map, just push it
          categories.value.splice(0, categories.value.length, ...Array.from(categoriesMap.values()))
        })
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        addNotification('error', e.message)
      } else {
        addNotification('error', 'An unknown error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  // download category items
  // download category items only when the category's items is empty
  const queryCategoryItems = async (categoryId: number): Promise<Registry[]> => {
    // if the categories is not initialized, just do it
    //console.log('registryStore: queryCategoryItems: categoryId: ', categoryId)
    if (categories.value.length === 0) {
      //console.log(
      //  'categories is empty, downloading categories to initialize: categories.value: ',
      //  categories.value
      //)
      // download categories first
      await queryCategories()
    }

    // test if it is already downloaded
    const category = categories.value.find((c: Category) => c.header.category === categoryId)
    //console.log('registryStore: category (', categoryId, ') : ', category)

    // don't want to download it again
    // the existence of category.items means it already downloaded, return
    if (category && category.items.length > 0) {
      // console.log(
      //   'using cached category. length of category.items and the items',
      //   category.items.length,
      //   category.items
      // )
      return category.items
    }

    setLoading(true)
    try {
      // if category is not found, fetch it
      //console.log('downloading category items: categoryId', categoryId)

      const data = await executeGraphQL(GET_CATEGORY_ITEMS, { categoryId })

      if (category) {
        if (data && data.getRegistryCategory) {
          // Assign fetched data to items
          category.items = [...data.getRegistryCategory]

          //console.log('Updated category items:', category.items)

          return category.items
        } else {
          throw new Error('Invalid response structure')
        }
      } else {
        console.error(`Category with ID ${categoryId} not found`)
        return []
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        addNotification('error', e.message)
      } else {
        addNotification('error', 'An unknown error occurred')
      }
      // Return an empty array instead of a single empty object
      return []
    } finally {
      setLoading(false)
    }
  }

  //
  const mutateRegistryItemStatus = async (updateRegistryItemStatusId: number, status: number) => {
    setLoading(true)
    try {
      const result = await executeGraphQL(UPDATE_REGISTRY_ITEM_STATUS, {
        trans_id: updateRegistryItemStatusId,
        status
      })

      if (result && result.updateRegistryItemStatus) {
        addNotification('info', 'Success!')
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        addNotification('error', e.message)
      } else {
        addNotification('error', 'An unknown error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  // Frontend API -------------------------------------------------------------

  // generate select options
  const comCategoryOptions = computed<SelectOptionType[]>(() =>
    categories.value.map((c: Category) => {
      const localeData = localeEntry(c.header)

      return {
        id: c.header.category,
        label: localeData?.code_value
      } as SelectOptionType
    })
  )

  //
  const comNextOrdering = computed(() => {
    const max = selectedCategory.value?.items.reduce(
      (max, c) => (c.ordering > max ? c.ordering : max),
      0
    )
    return 1 + (max ?? 0)
  })

  //
  const markRegistryTranslation = async (trans_id: number): Promise<number> => {
    setLoading(true)
    try {
      const data = await executeGraphQL(MARK_REGISTRY_TRANSLATIONS, { trans_id })
      let mark = 0

      // get the updated `mark` value from the server
      if (data && data.markRegistryTranslation) {
        mark = data.markRegistryTranslation ?? 0
      } else {
        throw new Error('Invalid response structure')
      }

      // update the mark in the editor
      return mark ?? 0
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'An unknown error occurred'
      addNotification('error', msg)

      return 0
    } finally {
      setLoading(false)
    }
  }

  //
  async function handleCategorySelected(categoryNumber: number) {
    await queryCategoryItems(categoryNumber)
    selectedCategory.value = categories.value.find(
      (item) => item.header.category === categoryNumber
    )

    selectedRegistry.value = selectedCategory.value?.header

    if (selectedRegistry.value) {
      selectedTranslation.value = localeEntry(selectedRegistry.value)
    }
  }

  //
  function handleRegistrySelected(selectedItem: Registry) {
    selectedRegistry.value = selectedCategory.value?.items.find(
      (item) => item.id === selectedItem.id
    )

    if (selectedRegistry.value) {
      selectedTranslation.value = localeEntry(selectedRegistry.value)
    }

    editor.setBuffer(selectedItem)
  }

  // Frontend API
  const comSelectedCategoryItems = computed<Registry[]>(() => {
    return selectedCategory.value?.items ?? ([] as Registry[])
  })

  const comRegistryOptions = async (
    categoryId: number,
    forId: string = 'code'
  ): Promise<SelectOptionType[]> => {
    // console.log('In comRegistryOptions: categoryId: ', categoryId)
    // console.log(
    //   'In comRegistryOptions: Before finding category, categories.value: ',
    //   categories.value
    // )

    const category = categories.value.find((c: Category) => c.header.category === categoryId)

    if (!category) {
      console.error(`Category with ID ${categoryId} not found`)
      return []
    }

    // console.log('In comRegistryOptions: category: ', category)
    // console.log('In comRegistryOptions: category.items: ', category.items)
    // console.log('In comRegistryOptions: category.items.length: ', category.items.length)

    // If category.items is empty, fetch the items
    if (category.items.length === 0) {
      //console.log('Category items are empty, fetching items for categoryId:  ', categoryId)
      await queryCategoryItems(categoryId)
    }

    // Re-check if category.items is now populated
    if (category.items.length > 0) {
      //console.log('Category items are populated: ', category.items)
      return category.items.map((c: Registry) => {
        const localeData = localeEntry(c)

        return {
          id: forId === 'code_value' ? localeData?.code_value.trim() : c.code.trim(),
          label: localeData?.code_value
        }
      })
    } else {
      console.error('Category items are still empty after fetching')
      return []
    }
  }

  // generate information of the selected category
  const comSelectedCategoryInformation = computed(() => {
    // the category object
    let categoryInfo: InfoCell[] | null = null
    const category: Category = selectedCategory.value ?? ({} as Category)
    const header: Registry = category ? category.header : ({} as Registry)

    if (category && header) {
      // find a translation
      const translation = localeEntry(header, getWorkingLanguageId())

      categoryInfo = [
        { name: 'code', label: 'Code', text: String(header.category) ?? 'N/A' },
        { name: 'type', label: '', text: header.code_type ?? 'N/A' },
        { name: 'count', label: 'Size', text: String(category.items.length) },
        { name: 'comment', label: '', text: translation?.comment ?? 'N/A' },
        { name: 'status', label: 'Status', text: header.status ? 'Enabled' : 'Disabled' },
        { name: 'modifiedAt', label: 'Modified', text: header.modifiedAt ?? 'N/A' }
      ]
    }

    return categoryInfo
  })

  // eslint-disable-next-line vue/return-in-computed-property
  //

  //
  const prepareNewCategory = () => {
    const category = editor.createEmptyCategory()

    selectedCategory.value = category
    selectedRegistry.value = category.header
    selectedRegistry.value.is_header = 1
    selectedRegistry.value.ordering = 0

    categories.value.push(category)

    editor.fillBufferWithInitialValue()
  }

  const prepareEditCategory = () => {
    //console.log('prepareEditCategory - selectedCategory.value', selectedCategory.value)

    // make sure selectedCategory.value is not undefined
    if (!selectedCategory.value) {
      //      console.error('selectedCategory.value is undefined')
      return
    } else {
      //    console.log('selectedCategory.value', selectedCategory.value)
      editor.setBuffer(selectedCategory.value?.header)
    }
  }

  //
  const prepareNewRegistryItem = () => {
    const registry = editor.createEmptyRegistry()

    if (selectedRegistry.value) {
      // put it in the same category
      registry.category = selectedRegistry.value?.category

      registry.ordering = comNextOrdering.value
      registry.code = String(registry.ordering)

      registry.translations[0].is_active = 1

      selectedCategory.value?.items.push(registry)
      selectedRegistry.value = registry
      selectedTranslation.value = registry.translations[0]

      // editor.category.items.push(buffer)
      // editor.registry = buffer
      // editor.translation = editor.registry.translations[0]
    }

    editor.fillBufferWithInitialValue()
  }

  // copy the selected translation
  const prepareNewTranslation = (item: RegistryTranslation) => {
    // copy & paste to the end of the translations
    const buffer = { ...item }

    // modify necessary fields
    buffer.id = 0
    buffer.is_active = 0
    buffer.code_value += ' copy'
    buffer.comment += ' copy'

    // append it to
    selectedRegistry.value?.translations.push(buffer)
    selectedTranslation.value = buffer
    //
    editor.fillBufferWithInitialValue()
  }

  ////////////////////////////////////////////////////////////////////////////////
  const manager = reactive({
    // task: ET.Show,
    setTask(task: ET) {
      ac.setTask(task)
    },

    //
    getTask() {
      return ac.currentTask.value
    }
  })

  ///////////////////////////////////////////////////////////////
  const editor = reactive({
    //task: ET.Show,

    buffer: {} as RegistryEditBuffer,

    //
    createEmptyEditorBuffer: (): RegistryEditBuffer => ({
      category: 0,
      is_header: 0,
      code_type: CodeType.INT,
      ordering: 0,
      code: '',
      language_id: 1,
      is_active: 0,
      code_value: '',
      comment: '',
      status: 0
    }),

    //
    emptyEditBuffer() {
      this.buffer = {
        category: 0,
        is_header: 0,
        ordering: 0,
        code_type: CodeType.INT,
        code: '',
        language_id: getWorkingLanguageId(),
        code_value: '',
        comment: '',
        is_active: 0,
        status: 0
      }
    },

    //
    setBuffer(reg: Registry) {
      this.buffer.category = reg.category
      this.buffer.is_header = reg.is_header
      this.buffer.ordering = reg.ordering
      this.buffer.code_type = reg.code_type
      this.buffer.code = reg.code
      this.buffer.language_id = reg.translations[0].language_id // getWorkingLanguageId()}
      this.buffer.code_value = reg.translations[0].code_value
      this.buffer.comment = reg.translations[0].comment
    },

    //
    accessDisabled(element: string) {
      switch (element) {
        case 'ordering':
        case 'code':
          return (
            (manager.getTask() === ET.NewTranslation || manager.getTask() === ET.EditTranslation) &&
            !selectedTranslation.value?.is_active
          )
        case 'code_value':
        case 'comment':
          if (manager.getTask() === ET.NewCategory || manager.getTask() === ET.NewItem) {
            return 0
          }
          return selectedTranslation.value?.is_active
        case 'delete category button': {
          // the button should be disabled until a category is selected
          return selectedCategory.value?.header ? 0 : 1
          // isEmptyObject(comSelectedCategory.value)
        }
      }
      return 0
    },

    //
    createEmptyRegistryTranslation(): RegistryTranslation {
      return {
        id: 0,
        registry_id: 0,
        language_id: getWorkingLanguageId(),
        is_active: 0,
        editor_id: 0,
        code_value: '',
        comment: '',
        createdAt: '',
        modifiedAt: '',
        mark: 0,
        status: 2
      }
    },

    //
    createEmptyRegistry(): Registry {
      return {
        id: 0,
        is_header: 0,
        category: 0,
        ordering: 0,
        code: '0',
        code_type: CodeType.INT,
        createdAt: '',
        modifiedAt: '',
        status: 2,
        translations: [this.createEmptyRegistryTranslation()]
      } as Registry
    },

    createEmptyCategory() {
      return {
        header: this.createEmptyRegistry(),
        items: []
      } as Category
    },

    //
    fillBufferWithInitialValue() {
      if (selectedRegistry.value && selectedTranslation.value) {
        this.buffer = {
          category: selectedRegistry.value.category,
          is_header: selectedRegistry.value.is_header,
          ordering: selectedRegistry.value.ordering,
          code_type: selectedRegistry.value.code_type,
          code: selectedRegistry.value.code,
          status: selectedRegistry.value.status,
          language_id: selectedTranslation.value.language_id,
          code_value: selectedTranslation.value.code_value,
          comment: selectedTranslation.value.comment,
          is_active: selectedTranslation.value.is_active
        }
      }

      // adjust if required
      switch (manager.getTask()) {
        case ET.NewCategory: {
          this.buffer.is_header = 1
          break
        }
        case ET.NewItem: {
          this.buffer.is_active = 1
          break
        }
      }
    },

    //
    getQueryDataWithEditBuffer(): UpdateRegistryInputType {
      // Update registry and translation with buffer data

      const data: UpdateRegistryInputType = {
        id:
          manager.getTask() === ET.NewCategory || manager.getTask() === ET.NewItem
            ? 0
            : (selectedRegistry.value?.id ?? 0),
        category: this.buffer?.category ? parseInt(this.buffer?.category.toString()) : 0,
        is_header: manager.getTask() === ET.NewCategory ? 1 : this.buffer.is_header,
        ordering: this.buffer?.ordering ? parseInt(this.buffer.ordering.toString()) : 0,
        code_type: this.buffer.code_type,
        code: this.buffer?.code ?? '0',
        status: this.buffer?.status ?? 0,
        translations: [
          {
            id:
              manager.getTask() === ET.NewCategory ||
              manager.getTask() === ET.NewItem ||
              manager.getTask() === ET.NewTranslation
                ? 0
                : (selectedTranslation.value?.id ?? 0),
            language_id:
              manager.getTask() === ET.NewCategory ||
              manager.getTask() === ET.NewItem ||
              manager.getTask() === ET.NewTranslation
                ? 1
                : (selectedTranslation.value?.language_id ?? getWorkingLanguageId()),
            code_value: this.buffer.code_value,
            comment: this.buffer.comment ?? '',
            status: 0
          }
        ]
      }
      return data
    },

    //
    async mutate() {
      setLoading(true)
      try {
        //
        const input = this.getQueryDataWithEditBuffer()

        //
        const data = await executeGraphQL(UPDATE_REGISTRY, { input })

        // console.log('task=', manager.getTask())
        // console.log('data=', data?.updateRegistry)

        if (data) {
          switch (manager.getTask()) {
            case ET.NewCategory: {
              if (data.updateRegistry && selectedCategory.value) {
                selectedCategory.value.header = data.updateRegistry
                selectedRegistry.value = data.updateRegistry
                selectedTranslation.value = selectedRegistry.value?.translations[0]

                // notify the user
                addNotification('info', 'New Category Created')
              }

              break
            }

            //
            case ET.EditCategory: {
              //console.log('Edit Category To Do')
              break
            }

            //
            case ET.NewItem: {
              selectedRegistry.value = data.updateRegistry
              selectedTranslation.value = selectedRegistry.value?.translations[0]

              const index = selectedCategory.value?.items.findIndex((item) => item.id === 0)

              if (index && index > -1 && selectedCategory.value) {
                selectedCategory.value.items[index] = data.updateRegistry
              }

              // notify the user
              addNotification('info', 'New Item Created')
              break
            }

            // Both EditTransaction & EditRegistry can be dealt same
            case ET.EditTranslation: {
              // transId from user input
              const transId = input.translations[0]?.id

              const trans = data.updateRegistry.translations.find(
                (i: RegistryTranslation) => i.id === transId
              )

              const targetIndex = selectedRegistry.value?.translations.findIndex(
                (i: RegistryTranslation) => i.id === transId
              )

              if (
                trans &&
                typeof targetIndex !== 'undefined' &&
                selectedRegistry.value &&
                selectedRegistry.value.translations
              ) {
                selectedRegistry.value.translations[targetIndex] = trans
              }

              // notify the user
              addNotification('info', 'Translation Updated')
              break
            }

            //
            case ET.EditItem: {
              selectedRegistry.value = data.updateRegistry

              break
            }

            //
            case ET.NewTranslation: {
              const transId = 0

              const trans = data.updateRegistry.translations.find(
                (i: RegistryTranslation) =>
                  i.code_value.trim() === input.translations[0].code_value.trim()
              )

              const targetIndex = selectedRegistry.value?.translations.findIndex(
                (i: RegistryTranslation) => i.id === transId
              )

              if (
                trans &&
                typeof targetIndex !== 'undefined' &&
                selectedRegistry.value &&
                selectedRegistry.value.translations
              ) {
                selectedRegistry.value.translations[targetIndex] = trans
              }

              // notify the user
              addNotification('info', 'New Translation Created')
              break
            }
          }
        }
        //
      } catch (e: unknown) {
        if (e instanceof Error) {
          addNotification('error', e.message)
        } else {
          addNotification('error', 'An unknown error occurred')
        }

        // throw new Error('Invalid response structure')
        throw new Error('Invalid response structure')
      } finally {
        setLoading(false)
      }
    },

    //
    async mark(trans_id: number) {
      const newMark = await markRegistryTranslation(trans_id)

      const reg = selectedRegistry.value?.translations.find((t) => t.id === trans_id)

      if (reg) {
        Object.assign(reg, { mark: newMark })
      }
    },

    async deleteTranslation(trans_id: number) {
      setLoading(true)
      try {
        const data = await executeGraphQL(DELETE_REGISTRY_TRANSLATION, {
          trans_id
        })

        // get the updated `mark` value from the server
        if (data && data.deleteRegistryTranslation && selectedRegistry.value) {
          const trans = selectedRegistry.value?.translations.filter((t) => t.id !== trans_id)
          selectedRegistry.value.translations = trans
        } else {
          throw new Error('Invalid response structure')
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'An unknown error occurred'
        addNotification('error', msg)
      } finally {
        setLoading(false)
      }
    },

    async deleteRegistry(registry_id: number) {
      setLoading(true)
      try {
        const data = await executeGraphQL(DELETE_REGISTRY, {
          id: Number(registry_id)
        })

        // get the updated `mark` value from the server
        if (data && data.deleteRegistry && selectedCategory.value) {
          // Remove the registry from the category items
          const reg = selectedCategory.value?.items.filter((r) => r.id !== registry_id)

          selectedCategory.value.items = reg
        } else {
          throw new Error('Invalid response structure')
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'An unknown error occurred'
        addNotification('error', msg)
      } finally {
        setLoading(false)
      }
    },

    //
    async deleteCategory(categoryNumber: number) {
      setLoading(true)
      try {
        const data = await executeGraphQL(DELETE_CATEGORY, {
          category: Number(categoryNumber)
        })

        if (data && data.deleteCategory) {
          const cat = categories.value.filter((c) => c.header.category !== categoryNumber)
          categories.value = cat

          selectedCategory.value = {} as Category
          selectedRegistry.value = {} as Registry
          selectedTranslation.value = {} as RegistryTranslation
        } else {
          throw new Error('Invalid response structure')
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'An unknown error occurred'
        addNotification('error', msg)
      } finally {
        setLoading(false)
      }
    },

    async activateTranslation(trans_id: number) {
      setLoading(true)
      try {
        const data = await executeGraphQL(ACTIVATE_REGISTRY_TRANSLATION, {
          trans_id
        })

        if (data && Number(data.activateRegistryTranslation) > 0) {
          // Iterate over each translation and update the is_active property
          selectedRegistry.value?.translations.forEach((translation) => {
            // Set is_active to true for the translation with the matching id
            Object.assign(translation, {
              is_active: Number(translation.id) === Number(trans_id)
            })
          })
          addNotification('info', 'Activation Success')
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          addNotification('error', e.message)
        } else {
          addNotification('error', 'An unknown error occurred')
        }
      } finally {
        setLoading(false)
      }
    },

    // do for canceled situation
    cleanup() {
      switch (manager.getTask()) {
        case ET.NewCategory:
          // eslint-disable-next-line no-case-declarations
          if (selectedCategory.value?.header.id === 0) {
            const cats = categories.value.filter((c) => c.header.id !== 0)
            categories.value = cats
          }
          break
        case ET.NewItem:
          if (selectedCategory.value && selectedRegistry.value?.id === 0) {
            const items = selectedCategory.value?.items.filter((r) => r.id !== 0)
            selectedCategory.value.items = items
          }
          break
        case ET.NewTranslation:
          if (selectedRegistry.value && selectedTranslation.value?.id === 0) {
            const trans = selectedRegistry.value?.translations.filter((t) => t.id !== 0)
            selectedRegistry.value.translations = trans
          }
          break
      }

      //
      manager.setTask(ET.Show)
    }
  }) // end of editor

  // ===================================================================
  //

  return {
    //
    //
    // category related ------------------
    queryCategories,
    queryCategoryItems,
    mutateRegistryItemStatus,
    categories,
    selectedCategory,
    selectedRegistry,
    selectedTranslation,
    comSelectedCategoryItems,
    comCategoryOptions,

    comRegistryOptions,
    //categoryComboBox,
    //comSelectedCategory,

    comSelectedCategoryInformation,

    handleCategorySelected,
    handleRegistrySelected,
    //    downloadCategoryItems,
    //    prepareSelectedCategory,
    //    prepareSelectedItem,
    //    prepareSelectedTranslation,

    prepareNewCategory,
    prepareNewRegistryItem,
    prepareNewTranslation,
    prepareEditCategory,

    // list
    //comSelectedCategoryItems,
    // //
    //
    //updateRegistryItemStatus,
    // editor ----------------------------
    manager,
    editor
    // getSelectedRegistryTranslations,
    // setEditorToCategoryMode,
    // setEditorToItemMode,
    // setEditorWithTranslation,
    // markRegistryTranslation,
    // updateRegistry,
    // activateRegistryTranslation,
    // setEditorToCopyTranslation,
    // deleteRegistryTranslation,
    // createEmptyRegistryTranslation,
    // createEmptyRegistry,
    // deleteRegistry
  }
})
